'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet, apiPost, type ApiRequestError } from '@/lib/api'
import {
  getAnyString,
  getData,
  getMessage,
  getQrPollingMessage,
  isSuccess,
  parseAccount,
  saveStoredAccount,
} from '@/lib/utils'
import type { Account, ApiResponse } from '@/types'

type UseLoginOptions = {
  onProfileRefresh: (account: Account) => Promise<void> | void
}

type UseLoginResult = {
  qrImage: string
  qrUuid: string
  loginStatus: string
  secureVerifyUrl: string
  loginLoading: boolean
  captcha: LoginImageCaptcha | null
  captchaSubmitting: boolean
  refreshQr: (options: LoginStartOptions) => Promise<void>
  submitCaptcha: (code: string) => Promise<void>
  resetLogin: () => void
}

/**
 * QR polling cadence.
 *
 * Go's `/login/CheckLoginStatus` is a plain synchronous endpoint (no SSE / long-poll)
 * so we have to poll. To avoid hammering it every 1 s for minutes on end:
 *
 *   - While waiting for scan (status 0): start at 2 s and exponentially back
 *     off to BACKOFF_MAX_MS. Most users scan within 10–30 s; after a minute
 *     of idling the cadence settles near the 15 s ceiling.
 *   - While awaiting phone-side confirmation (status 1): snap back to the
 *     FAST_POLL_MS cadence since that phase resolves quickly (a few seconds).
 *   - When the tab is hidden: stop polling entirely. Resume immediately on
 *     visibility change.
 *   - QR expiry is controlled by wxproto's Data.ExpiredTime from the QR
 *     response; when it reaches zero we automatically request a fresh QR code.
 */
const INITIAL_POLL_MS = 2000
const FAST_POLL_MS = 1500
const BACKOFF_FACTOR = 1.5
const BACKOFF_MAX_MS = 15_000

export type LoginMethod = 'secondary' | 'direct'

export type LoginStartOptions = {
  region: string
  method: LoginMethod
  /** Re-login target; empty means "log in a new WeChat account". */
  wxid?: string
}

type CheckState = 'waiting' | 'scanned' | 'confirmed' | 'done'
type LoginPhase = 'idle' | 'loading' | 'waiting' | 'scanned' | 'captcha' | 'secure' | 'error' | 'expired'

export type LoginImageCaptcha = {
  type: 'wtlogin_image_captcha'
  uuid: string
  imgSid: string
  imageData: string
}

function parsePollState(response: ApiResponse): { state: CheckState; statusText: string } {
  const data = getData(response)
  const statusText = getAnyString(data, ['state', 'State', 'status', 'Status'])
  const status = statusText ? Number(statusText) : null
  if (isSuccess(response) && parseAccount(data)) return { state: 'done', statusText }
  if (status === 2) return { state: 'confirmed', statusText }
  if (status === 1) return { state: 'scanned', statusText }
  return { state: 'waiting', statusText }
}

function parseExpiredTime(value: unknown): number {
  if (typeof value !== 'string') return 0
  const text = value.trim()
  const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/.exec(text)
  if (!match) return 0
  const [, year, month, day, hour, minute, second] = match
  return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)).getTime()
}

function formatQrCountdown(expiresAt: number): string {
  const remainingSeconds = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))
  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60
  return minutes > 0
    ? `二维码剩余有效期 ${minutes}:${String(seconds).padStart(2, '0')}`
    : `二维码剩余有效期 ${seconds} 秒`
}

function needsImageCaptcha(response: ApiResponse): boolean {
  return response.Code === -6 || response.code === -6
}

function needsSecureVerify(response: ApiResponse): boolean {
  const data = getObjectData(response)
  return (response.Code === -3 || response.code === -3) && Boolean(data.ticketUrl || data.TicketUrl)
}

function parseImageCaptcha(response: ApiResponse): LoginImageCaptcha | null {
  const data = getData(response)
  if (!needsImageCaptcha(response) || !data || typeof data !== 'object') return null
  const object = data as Record<string, unknown>
  if (object.type !== 'wtlogin_image_captcha') return null
  const imageData = String(object.imageData || '')
  if (!imageData) return null
  return {
    type: 'wtlogin_image_captcha',
    uuid: String(object.uuid || ''),
    imgSid: String(object.imgSid || ''),
    imageData,
  }
}

function getObjectData(response: ApiResponse): Record<string, unknown> {
  const data = getData(response)
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
}

export function useLogin(
  account: Account | null,
  setAccount: (next: Account | null) => void,
  options: UseLoginOptions,
): UseLoginResult {
  const [qrImage, setQrImage] = useState('')
  const [qrUuid, setQrUuid] = useState('')
  const [loginStatus, setLoginStatus] = useState('请选择地区和登录方式后点击登录')
  const [secureVerifyUrl, setSecureVerifyUrl] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [captcha, setCaptcha] = useState<LoginImageCaptcha | null>(null)
  const [captchaSubmitting, setCaptchaSubmitting] = useState(false)
  const [qrExpiresAt, setQrExpiresAt] = useState(0)
  const [loginPhase, setLoginPhase] = useState<LoginPhase>('idle')
  const lastStartOptionsRef = useRef<LoginStartOptions | null>(null)
  // 一次登录的每个请求都要带上它：网关按 wxid 定位协议 key，漏带会落到另一把
  // 从没出过码的 key 上，wxproto 恒回“逻辑错误！”。
  const loginWxidRef = useRef('')

  const { onProfileRefresh } = options

  const completeLogin = useCallback(
    async (response: ApiResponse) => {
      const parsed = parseAccount(getData(response))
      if (!parsed) return false

      // 不能 silent：协议侧此刻已经登录成功（手机上已能看到设备），但控制台要
      // 靠这条绑定才查得到该账号。静默吞掉失败的表现就是"手机显示已登录、后台
      // 却看不到"，且没有任何线索。失败原因由 apiPost 自己弹出。
      try {
        const bound = await apiPost<{ success?: boolean; Success?: boolean }>('/accounts', {
          wxid: parsed.wxid,
          nickName: parsed.nickName,
          avatar: parsed.avatar || '',
        })
        if (bound?.success === false || bound?.Success === false) {
          setLoginPhase('idle')
          return false
        }
      } catch {
        setLoginPhase('idle')
        return false
      }

      saveStoredAccount(parsed)
      setAccount(parsed)
      loginWxidRef.current = ''
      setCaptcha(null)
      setSecureVerifyUrl('')
      setQrExpiresAt(0)
      setLoginPhase('idle')
      void onProfileRefresh(parsed)
      return true
    },
    [onProfileRefresh, setAccount],
  )

  const refreshQr = useCallback(async ({ region, method, wxid }: LoginStartOptions) => {
    const loginRegion = region || '1'
    const targetWxid = wxid?.trim() || ''
    lastStartOptionsRef.current = { region: loginRegion, method, wxid: targetWxid }
    loginWxidRef.current = targetWxid
    setLoginLoading(true)
    setLoginPhase('loading')
    setLoginStatus('正在获取二维码...')
    setSecureVerifyUrl('')
    setCaptcha(null)
    setQrImage('')
    setQrUuid('')
    setQrExpiresAt(0)
    try {
      const requestBody: Record<string, unknown> = {
        Check: false,
        IpadOrmac: method === 'direct' ? 'mac' : '',
        Proxy: loginRegion,
      }
      // Naming the wxid makes Gateway reuse that account's existing protocol
      // key; without it every re-login mints a new key for the same account.
      if (targetWxid) requestBody.Wxid = targetWxid

      const response = await apiPost<ApiResponse<Record<string, unknown>>>(
        '/login/GetLoginQrCodeNewDirect',
        requestBody,
        { silent: true },
      )
      const data = getData(response) || {}
      // 网关发现 reserved key 背后已有本人在线会话时会直接认领并返回
      // alreadyLoggedIn 标记——无需扫码，直接完成登录。
      if (isSuccess(response) && (data as Record<string, unknown>).alreadyLoggedIn === true) {
        const done = await completeLogin(response)
        if (done) {
          setLoginStatus('该微信号已在线，已自动恢复登录')
          return
        }
      }
      const image = String(data.QrCodeUrl || '')
      if (!isSuccess(response) || !image || !data.ExpiredTime) {
        setQrImage('')
        setQrUuid('')
        setQrExpiresAt(0)
        setLoginPhase('error')
        setLoginStatus(getMessage(response) || '获取二维码失败：二维码地址或过期时间缺失')
        return
      }
      const expiresAt = parseExpiredTime(data.ExpiredTime)
      if (!expiresAt) {
        setQrImage('')
        setQrUuid('')
        setQrExpiresAt(0)
        setLoginPhase('error')
        setLoginStatus('获取二维码失败：缺少过期时间')
        return
      }
      setQrImage(
        image.startsWith('data:image') || /^https?:\/\//i.test(image) ? image : `data:image/png;base64,${image}`,
      )
      setQrUuid('login-status')
      setQrExpiresAt(expiresAt)
      setLoginPhase('waiting')
      setLoginStatus(formatQrCountdown(expiresAt))
    } catch (error) {
      setLoginPhase('error')
      setLoginStatus(error instanceof Error ? error.message : '获取二维码失败')
      setQrImage('')
      setQrUuid('')
      setQrExpiresAt(0)
      setSecureVerifyUrl('')
    } finally {
      setLoginLoading(false)
    }
  }, [completeLogin])

  useEffect(() => {
    if (!qrUuid || account || loginPhase !== 'waiting' || !qrExpiresAt) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const tick = () => {
      if (cancelled) return
      const remainingMs = qrExpiresAt - Date.now()
      if (remainingMs <= 0) {
        const lastOptions = lastStartOptionsRef.current
        setLoginStatus('二维码已过期，正在重新获取...')
        if (lastOptions) {
          void refreshQr(lastOptions)
        } else {
          setQrImage('')
          setQrUuid('')
          setQrExpiresAt(0)
          setLoginPhase('expired')
        }
        return
      }
      setLoginStatus(formatQrCountdown(qrExpiresAt))
      timer = setTimeout(tick, remainingMs > 60_000 ? 1000 : 500)
    }

    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [account, loginPhase, qrExpiresAt, qrUuid, refreshQr])

  // Adaptive poller. Replaces the old fixed 1 s setInterval.
  useEffect(() => {
    if (!qrUuid || account) return

    // Mutable loop state kept in refs so we can cancel cleanly.
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let interval = INITIAL_POLL_MS
    const schedule = (delay: number) => {
      if (cancelled) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(tick, delay)
    }

    const tick = async () => {
      if (cancelled) return

      // Pause polling while the tab is hidden. We re-schedule ourselves
      // from the visibilitychange listener once the tab comes back.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return
      }

      let response: ApiResponse
      try {
        // {silent:true} so a transient 4xx/5xx doesn't fire an error
        // toast on every tick — we already mirror the reason into the
        // inline status text below.
        const target = loginWxidRef.current
        response = await apiGet<ApiResponse>(
          target ? `/login/CheckLoginStatus?wxid=${encodeURIComponent(target)}` : '/login/CheckLoginStatus',
          { silent: true },
        )
      } catch (error) {
        // A wrong-account scan is terminal for this QR: Gateway already tore the
        // session down, so retrying would only poll a login that cannot finish.
        if ((error as ApiRequestError)?.code === 'LOGIN_WXID_MISMATCH') {
          setLoginPhase('error')
          setLoginStatus(error instanceof Error ? error.message : '扫码的微信号与所选账号不一致')
          setQrImage('')
          setQrUuid('')
          setQrExpiresAt(0)
          return
        }
        // Network blip — back off and retry.
        interval = Math.min(interval * BACKOFF_FACTOR, BACKOFF_MAX_MS)
        schedule(interval)
        return
      }

      if (cancelled) return

      if (needsImageCaptcha(response)) {
        const nextCaptcha = parseImageCaptcha(response)
        setCaptcha(nextCaptcha)
        setSecureVerifyUrl('')
        setLoginPhase('captcha')
        setLoginStatus(nextCaptcha ? '请输入图片验证码' : getQrPollingMessage(response))
        return
      }

      if (needsSecureVerify(response)) {
        const data = getObjectData(response)
        const ticketUrl = String(data.ticketUrl || data.TicketUrl || '')
        setSecureVerifyUrl(ticketUrl)
        setLoginPhase('secure')
        setLoginStatus(getMessage(response) || '需要提交验证码后登录')
        interval = FAST_POLL_MS
        schedule(interval)
        return
      }

      setSecureVerifyUrl('')
      const { state } = parsePollState(response)
      if (state === 'done') {
        await completeLogin(response)
        return
      }

      // Do not treat wxproto's successful waiting response as scanned. Until
      // CheckLoginStatus reports state/status 1 or 2, keep the ExpiredTime
      // countdown as the visible status.
      if (state === 'scanned' || state === 'confirmed') {
        setLoginPhase('scanned')
        setLoginStatus(getQrPollingMessage(response))
        interval = FAST_POLL_MS
      } else {
        interval = Math.min(interval * BACKOFF_FACTOR, BACKOFF_MAX_MS)
      }
      schedule(interval)
    }

    const onVisibilityChange = () => {
      if (cancelled) return
      if (document.visibilityState === 'visible') {
        // Snap back to a reasonable cadence when the user refocuses,
        // so they don't stare at a stale "等待扫码" for 15 s.
        interval = INITIAL_POLL_MS
        schedule(0)
      }
    }

    // Kick off the first poll ASAP — don't make the user wait INITIAL_POLL_MS.
    schedule(0)

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange)
    }

    return () => {
      cancelled = true
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange)
      }
    }
  }, [account, completeLogin, qrExpiresAt, qrUuid, refreshQr])

  const resetLogin = useCallback(() => {
    loginWxidRef.current = ''
    lastStartOptionsRef.current = null
    setQrImage('')
    setQrUuid('')
    setLoginStatus('请选择地区和登录方式后点击登录')
    setSecureVerifyUrl('')
    setCaptcha(null)
    setCaptchaSubmitting(false)
    setLoginLoading(false)
    setQrExpiresAt(0)
    setLoginPhase('idle')
  }, [])

  const submitCaptcha = useCallback(
    async (code: string) => {
      const trimmed = code.trim()
      if (!captcha || !trimmed) {
        setLoginStatus('请输入图片验证码')
        return
      }
      setCaptchaSubmitting(true)
      setLoginPhase('captcha')
      setLoginStatus('正在提交验证码...')
      try {
        const response = await apiPost<ApiResponse>(
          '/login/VerifyCode',
          {
            Uuid: captcha.uuid || qrUuid,
            Code: trimmed,
            ...(loginWxidRef.current ? { Wxid: loginWxidRef.current } : {}),
          },
          { silent: true },
        )
        if (needsImageCaptcha(response)) {
          const nextCaptcha = parseImageCaptcha(response)
          setCaptcha(nextCaptcha || captcha)
          setLoginPhase('captcha')
          setLoginStatus(nextCaptcha ? '验证码不正确，请重新输入' : getQrPollingMessage(response))
          return
        }
        if (await completeLogin(response)) return
        setLoginPhase('error')
        setLoginStatus(getMessage(response) || '验证码提交失败')
      } catch (error) {
        setLoginPhase('error')
        setLoginStatus(error instanceof Error ? error.message : '验证码提交失败')
      } finally {
        setCaptchaSubmitting(false)
      }
    },
    [captcha, completeLogin, qrUuid],
  )

  return {
    qrImage,
    qrUuid,
    loginStatus,
    secureVerifyUrl,
    loginLoading,
    captcha,
    captchaSubmitting,
    refreshQr,
    submitCaptcha,
    resetLogin,
  }
}
